// Pure helper for the Segment-title radial x-offset (WEB-036 fix,
// audits/FINDINGS-REGISTER.md).
//
// Kept in its own dependency-free module (no `d3` import) rather than inline
// in createPie.js so it stays independently unit-testable: createPie.js
// imports the whole `d3` package, which is ESM-only and isn't transformed by
// this repo's default Jest config, so any test that imports createPie.js
// directly fails to parse. A test importing only this module sidesteps that
// entirely.
export function computeLabelRadialX(outerRadius, midangle) {
  return outerRadius * 0.99 * (midangle < Math.PI ? 1 : -1);
}

// hw-0914-dev-label-pill-clipped-offscreen-1280: a rim-pill's horizontal
// anchor (`pillTx`, derived from `self.pieOutterRadius`) is computed
// independently of the Builder pane's OWN current width. Selecting a segment
// can shrink the pane (BUILDER_SEGMENT_THIRDS) in the same tick that
// createPie's selection-only fast path (syncLabelSelectionTransforms)
// recomputes every OTHER segment's anchor — if `pieOutterRadius` hasn't
// caught up to the pane's new, smaller width yet, the anchor itself (not
// just the label text, which COMPB-02 already clamps) can land outside the
// pane, pushing the whole pill off the visible canvas. MEASURED live
// (web-dev.iyuga.net, 1280x720, fresh 6-segment wheel): a non-selected
// segment's pill settled at screen left:-16/-18 and stayed there
// indefinitely (not a transient race) after a sibling segment was selected.
// Mirrors COMPB-02's existing `paneWidth/2` boundary (segmentContrastGuard.js
// clampCharsToAvailableWidth) so both the anchor's POSITION and its label's
// WIDTH are bounded to the same pane, instead of only the width.
export function clampAnchorTxToPane(tx, paneWidth) {
  if (
    typeof paneWidth !== "number" ||
    !Number.isFinite(paneWidth) ||
    paneWidth <= 0 ||
    typeof tx !== "number" ||
    !Number.isFinite(tx)
  ) {
    return tx;
  }
  const maxTx = paneWidth / 2;
  return Math.max(-maxTx, Math.min(maxTx, tx));
}

// hw0918b-selected-segment-rim-pill-clipped-by-canvas-card-1440: clampAnchorTxToPane
// above only bounds the ANCHOR point — the rendered pill BOX (rect x/width around
// that anchor) can still stick out past the pane even when the anchor itself is
// legally inside it. Shared by both createPie.js render paths that can move a pill
// (loadRectSize()'s full rebuild AND syncLabelSelectionTransforms()'s selection-only
// fast path) so a segment click alone — which never runs loadRectSize() — still gets
// the box nudged back inside self.parent's current width.
//
// gordon-0923-P0-REPEAT-web-rim-labels-overlap-the-wheel-never-allowed: this
// function is still used for the FIRST pane clamp at each call site (BEFORE
// ring-safety runs) — unchanged. createPie.js used to call it a SECOND time
// AFTER ring-safety too ("the final re-clamp"), to keep a pill from landing
// fully off-pane; that second call is what pulled two real labels back onto
// the wheel by -24.8px/-33.1px, live-measured on /builder/4908. It has been
// removed — see createPie.js's updateLable()/loadRectSize() for the full
// write-up on why ring-safety's placement must never be undone, and how the
// upstream text-width shrink (pillRawTx) takes over the "never hidden" job
// this second call used to do.
export function clampPillBoxToPane(tx, rectX, rectWidth, paneWidth) {
  if (
    typeof paneWidth !== "number" ||
    !Number.isFinite(paneWidth) ||
    paneWidth <= 0 ||
    typeof tx !== "number" ||
    !Number.isFinite(tx) ||
    typeof rectX !== "number" ||
    !Number.isFinite(rectX) ||
    typeof rectWidth !== "number" ||
    !Number.isFinite(rectWidth)
  ) {
    return tx;
  }
  const halfPane = paneWidth / 2;
  let nudge = 0;
  // Right edge first, then left — so a pill wider than the container ends up
  // flush with the LEFT edge rather than the right.
  const rightOverflow = tx + rectX + rectWidth - halfPane;
  if (rightOverflow > 0) nudge -= rightOverflow;
  const leftOverflow = -halfPane - (tx + rectX + nudge);
  if (leftOverflow > 0) nudge += leftOverflow;
  return tx + nudge;
}

// gv-0914c-dev-label-pills-clip-off-the-left-edge-of-the-canvas-below-900px
// (r1006 re-measure, 2026-10-06): clampPillBoxToPane's `paneWidth` argument
// (createPie.js loadRectSize()) was always `self.parent.clientWidth` -- the
// WheelComponent's OWN square root div -- not the surface a pill actually
// gets clipped by. On the Builder, self.parent sits INSET inside the real
// clip boundary (`builder-canvas-pane`, `overflow:hidden`) by a fixed side
// margin (~28px per side MEASURED live web-dev.iyuga.net, fresh 6-segment
// wheel 7555, 900px viewport: self.parent 464px wide vs the pane's own
// 520px, both centred on the same point). Clamping to the NARROWER
// self.parent boundary left ring-safety's later, unconditional
// corner-clearance push (gordon-0923-P0-REPEAT -- never overlap the wheel,
// even past the pane) nowhere to land but past the pane's REAL edge for a
// tall, multi-line-wrapped title (wrapLabelIntoLines, #5546, is deliberately
// unbounded in line count, so a long title's box can be tall enough that
// clearing the ring by WHEEL_PILL_RIM_GAP on every corner needs more lateral
// room than self.parent's own inset box has, even though the ACTUAL
// clipping surface has plenty). This does not touch ring-safety's placement
// or re-clamp anything AFTER it runs (the two things #5544/#6379's write-ups
// warn never to redo) -- it only widens the EARLIER, uncontroversial
// pre-ring-safety clamp to match the real clip edge, giving ring-safety more
// legitimate room to push into before it has to overshoot it.
export function findClipBoundaryWidth(parentEl) {
  const fallback =
    parentEl && typeof parentEl.clientWidth === "number"
      ? parentEl.clientWidth
      : null;
  if (
    !parentEl ||
    typeof parentEl.parentElement === "undefined" ||
    typeof window === "undefined" ||
    typeof window.getComputedStyle !== "function"
  ) {
    return fallback;
  }
  let el = parentEl;
  for (let i = 0; i < 8 && el; i++) {
    let style;
    try {
      style = window.getComputedStyle(el);
    } catch (e) {
      break;
    }
    if (
      style &&
      (style.overflow === "hidden" ||
        style.overflowX === "hidden" ||
        style.overflow === "clip" ||
        style.overflowX === "clip")
    ) {
      return el.clientWidth;
    }
    el = el.parentElement;
  }
  return fallback;
}

// stgfix-1008-NEW-EditWheel-2-right-rimpill-clip-sub900: the FINAL,
// post-ring-safety pass (createPie.js loadRectSize(), runViewportSafetyCorrectionLoop
// -- see its own call site comment) only ever pulled a pushed-past pill back
// inside `window.innerWidth/innerHeight` -- never back inside the REAL clip
// boundary this module's own findClipBoundaryWidth() above already knows how
// to find (the nearest overflow:hidden/clip ancestor, e.g. the Builder's
// `builder-canvas-pane`). Below 900px, opening the Wheel Settings/Segment
// pane narrows that ancestor well short of the actual browser window
// (computePaneWidths() gives the canvas only 70%/75% of the space left after
// the rail) -- MEASURED LIVE web-stg.iyuga.net main.e3974551.js, /builder/
// 507379 (12-seg iTOVi-stress fixture) at 899/860/820/768px: right-side rim
// pills ("Basal Metabolic Rate Estim...", "Hydration...") render with their
// real screen box running past that narrower pane edge while still
// comfortably inside window.innerWidth, so the window-only clamp never
// engages and the pane's own `overflow:hidden` visually clips them.
//
// This is the SAME walk as findClipBoundaryWidth, just returning the
// ancestor's own getBoundingClientRect() instead of its clientWidth, so a
// caller can intersect it with the window bounds for the FINAL pass the way
// findClipBoundaryWidth's result already feeds the EARLIER, pre-ring-safety
// clamp (clampPillBoxToPane). Returns null (never throws) wherever
// findClipBoundaryWidth would have fallen back to parentEl.clientWidth with
// no real boundary element found -- callers must treat null as "no tighter
// bound than the window".
export function findClipBoundaryRect(parentEl) {
  if (
    !parentEl ||
    typeof parentEl.parentElement === "undefined" ||
    typeof window === "undefined" ||
    typeof window.getComputedStyle !== "function"
  ) {
    return null;
  }
  let el = parentEl;
  for (let i = 0; i < 8 && el; i++) {
    let style;
    try {
      style = window.getComputedStyle(el);
    } catch (e) {
      break;
    }
    if (
      style &&
      (style.overflow === "hidden" ||
        style.overflowX === "hidden" ||
        style.overflow === "clip" ||
        style.overflowX === "clip")
    ) {
      return typeof el.getBoundingClientRect === "function"
        ? el.getBoundingClientRect()
        : null;
    }
    el = el.parentElement;
  }
  return null;
}
